// - フリーテキストの入力情報 -
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import iconDropper from '../../../../../assets/img/icon/dropper.svg';
import './trim.free-text.scss';
import { Button } from '../../../../ui/button/button';
import { Pos } from '../../../../../models/pos';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import useDraggable from '../../../../../hooks/draggable';
import { FontSizeList } from '../../../../../layout-editor/model/font-size-list';
import { TextAlignList } from '../../../../../layout-editor/model/text-align-list';
import { TextRotationList } from '../../../../../layout-editor/model/text-rotation-list';
import { TrimUiManager } from '../../../../../trim-editor/manager/ui/trim.ui.manager';
import { dialogActions } from '../../../../dialog/slice/dialog-slice';
import { ColorDropper } from './trim.color-dropper-dialog';
import { ColorCodeConverter, RgbaData } from '../../../../../utilities/colorcode-converter';
import { AwaitingEllipsis } from '../../../../ui/awaiting/awaiting-ellipsis';
import { TrimLocalFontLoaderErrorDialog } from './trim.local-font-loader-error-dialog';
import { ColorPicker } from '../../../../ui/input/color-picker';
import { Radiobutton } from '../../../../ui/input/radiobutton';
import { DynamicTooltip } from '../../../../ui/tooltip/dynamic-tooltip';
import { Select } from '../../../../ui/select/select';
import { Input } from '../../../../ui/input/input';
import { NumUpDown } from '../../../../ui/input/num-up-down';
import { Checkbox } from '../../../../ui/input/checkbox';
import { TextArea } from '../../../../ui/textarea/textarea';
import { TrimTextAlignStrType } from '../../../../../trim-editor/manager/image-edit/text/trim.text-controller';


// - フリーテキストの入力情報 -
export type TrimTextInfo = {
  id: string,
  isBold: boolean,
  isItalic: boolean,
  textColor: string,
  isOnDropper: boolean,
  fontType: 'local' | 'server' | '',
  font: string,
  fontSize: string,
  textTransparency: number,
  dropShadow: boolean,
  transparency: number,
  distance: number,
  outlineWidth: number,
  outlineColor: string,
  textAlign: string,
  textRotation: number,
  textSelectRotation: number,
  lineSpacingNum: number,
  textAreaContents: string,
  height: number,
  pos?: Pos & { scale: number },
  adjustedPos?: Pos & { scale: number },
};
export type TrimTextFont = {
  isBold: string,
  isItalic: string,
  size: string,
  family: string,
}
export type TrimTextOption = {
  fontType: '' | 'local' | 'server',
  isShadow: boolean,
  shadowTransparency: string,
  offset: number,
  outlineColor: string,
  outline: number,
}
type FreeTextProps = {
  // - 入力情報を取得 -
  info: TrimTextInfo;
  // onGetTextInfo: (info: TextInfo) => void,
  popCallback: () => void,
  style?: CSSProperties,
  onBlur?: boolean,
};
// - フリーテキスト表示 -
export const TrimFreeText = (props: FreeTextProps) => {
  const { info, style, popCallback, onBlur } = props;
  const initFontList = useAppSelector((v) => v.layout.initFontList);
  const userAgent = window.navigator.userAgent.toLowerCase();
  // - dispatch -
  const dispatch = useAppDispatch();
  // - Ref -
  const container = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const localFontRadioButtonEle = useRef<HTMLInputElement>(null);
  // - Hooks -
  useDraggable(body);
  // useDraggable(container);
  // - State -
  // -- ID --
  const [id, setId] = useState(info.id);
  // -- 太字 --
  const [isBold, setIsBold] = useState(info.isBold);
  // -- イタリック体 --
  const [isItalic, setIsItalic] = useState(info.isItalic);
  // -- 文字色 --
  const [textColor, setTextColor] = useState(info.textColor);
  // -- スポイト --
  const [isOnDropper, setIsOnDropper] = useState(info.isOnDropper);
  // -- フォント --
  const [font, setFont] = useState(info.font);
  // -- フォントリスト --
  const [fontList, setFontList] = useState<{ value: string, label: string }[]>(info.fontType === 'server' ? initFontList : []);
  // -- 文字サイズ --
  const [fontSize, setFontSize] = useState(info.fontSize + 'pt');
  const [fontSizeView, setFontSizeView] = useState(info.fontSize + 'pt');
  // -- 文字サイズリスト --
  const [fontSizeList, setFontSizeList] = useState<{ value: string, label: string }[]>([...FontSizeList]);
  // -- 文字透明度 --
  const [textTransparency, setTextTransparency] = useState(info.textTransparency);
  // -- ドロップシャドウ --
  const [dropShadow, setDropShadow] = useState(info.dropShadow);
  // -- 透明度 --
  const [transparency, setTransparency] = useState(info.transparency);
  // -- 距離 --
  const [distance, setDistance] = useState(info.distance);
  // -- 文字アウトライン幅 --
  const [outlineWidth, setOutlineWidth] = useState(info.outlineWidth);
  // -- 文字アウトライン色 --
  const [outlineColor, setOutlineColor] = useState(info.outlineColor);
  // -- 文字揃え --
  const [textAlign, setTextAlign] = useState(info.textAlign);
  // -- 文字揃えリスト --
  const [textAlignList, setTextAlignList] = useState([...TextAlignList]);
  // -- 文字回転 --
  const [textSelectRotation, setTextSelectRotation] = useState(String(info.textSelectRotation));
  // -- 文字回転 --
  const [textRotation, setTextRotation] = useState(String(info.textRotation));
  // -- 文字回転リスト --
  const [textRotationList, setTextRotationList] = useState([...TextRotationList]);
  // -- 行間調整 --
  const [lineSpacingNum, setLineSpacingNum] = useState(info.lineSpacingNum);
  // -- テキストエリア --
  const [textAreaContents, setTextAreaContents] = useState(info.textAreaContents);
  // -- テキスト反映ボタン制御フラグ --
  const [reloadFlag, setReloadFlag] = useState(false);
  // -- Chromeブラウザかの判定 --
  const [isChrome, setIsChrome] = useState(true);
  // -- ローカルフォントに関するアラートフラグ --
  const [localFontAlert, setLocalFontAlert] = useState(false);
  // -- テキストエリア以外の更新フラグ --
  const [isChange, setIsChange] = useState(false);
  // -- 更新ボタンを最後に押したときのテキスト --
  const [prevText, setPrevText] = useState('');
  // -- 調整後 top --
  const [fixedTop, setFixedTop] = useState(0);
  // -- 調整後 right --
  const [fixedRight, setFixedRight] = useState(0);
  // -- 初期状態か --
  const [isInit, setIsInit] = useState(true);
  // - Callback -
  // -- 太字 --
  const handlerClickBold = useCallback(() => {
    setIsBold(prev => !prev);
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // -- イタリック体 --
  const handlerClickItalic = useCallback(() => {
    setIsItalic(prev => !prev);
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // -- スポイト --
  // -- スポイトの終了 --
  const stopDropper = (): Promise<void> => new Promise((resolve, reject) => {
    TrimUiManager.ins.emit('r->t:color-dropper:stop', {});
    document.body.removeEventListener('mousedown', selectDropper);
    setTimeout(() => {
      resolve();
      setReloadFlag(true);
      setIsChange(true);
    }, 500);
  });
  // -- スポイトでの色の確定 --
  const selectDropper = useCallback(async () => {
    await stopDropper();
    setIsOnDropper(false);
  }, []);
  // -- スポイトスタート --
  const handlerClickDropper = useCallback((v) => {
    setIsOnDropper(v);
    if (v) {
      document.body.addEventListener('mousedown', selectDropper);
      dispatch(dialogActions.push({
        title: '',
        element: <ColorDropper
          onChange={(e) => {
            const colorData: RgbaData = ColorCodeConverter.rgbaToColorCode(e.color);
            setTextColor(colorData.colorCode);
            setIsChange(true);
          }}
        />,
        onWillClose: () => {
          TrimUiManager.ins.emit('r->t:color-dropper:stop', {});
        },
        className: 'dropper_dialog',
        closeBtn: true,
      }));
    }
  }, [textColor, setTextColor]);
  // -- フォント --
  const handlerChangeFontType = useCallback((v) => {
      dispatch(dialogActions.push({
        title: '',
        element: <div>フォントを読み込んでいます<AwaitingEllipsis
          color="#888"
          size="12px"
        /></div>,
        className: 'loading_font_dialog',
      }));
      TrimUiManager.ins.emit('t->r:web-font:download', {
        fontFamilyName: v,
        callback: () => {
          dispatch(dialogActions.pop());
          setFont(v);
          setReloadFlag(true);
          setIsChange(true);
        },
        error: (msg: string) => {
          dispatch(dialogActions.pop());
          setReloadFlag(true);
          setIsChange(true);
          alert('フォントの読み込みに失敗しました。');
        },
      });
  }, []);
  // -- 文字サイズ --
  const handlerChangeTextSize = useCallback((v) => {
    const font = fontSizeList.find((w) => w.value === v)!.label;
    setFontSize(font);
    setFontSizeView(font);
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  const handlerBlurFontSize = useCallback((v) => {
    const fixText = v.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s: string) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    }).replace(/[^0-9]/gi, '');
    if (Number(fixText) < 1) {
      setFontSize('1' + 'pt');
      setFontSizeView('1' + 'pt');
    } else if (Number(fixText) > 600) {
      setFontSize('600' + 'pt');
      setFontSizeView('600' + 'pt');
    } else {
      setFontSize(fixText + 'pt');
      setFontSizeView(fixText + 'pt');
    }
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // フォントサイズが選択肢の中から選ばれていたら値を入れる。
  const selectFontSize = useMemo(() => {
    return fontSizeList.find((v) => v.label === fontSize);

  }, [fontSizeList, fontSize])
  // -- 文字透明度 --
  const handlerChangeTextTransparency = useCallback((v) => {
    setTextTransparency(v);
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // -- 削除ボタン --
  const handlerClickDelete = useCallback(() => {
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: ['この文字情報を削除します。よろしいですか？'],
      buttons: [
        {
          label: 'いいえ',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        },
        {
          label: 'はい',
          callback: () => {
            TrimUiManager.ins.emit('r->t:delete-text', ({ id }));
            setReloadFlag(false);
            setIsChange(false);
            popCallback && popCallback();
            dispatch(dialogActions.pop());
          },
        },
      ],
    }));

  }, [id]);
  // -- 文字アウトライン幅 --
  const handlerChangeOutlineWidth = useCallback((v) => {
    setOutlineWidth(v);
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // -- 文字揃え --
  const handlerChangeTextAlign = useCallback((v) => {
    switch (v) {
      case 'left':
        setTextAlign('0');
        break;
      case 'center':
        setTextAlign('1');
        break;
      case 'right':
        setTextAlign('2');
        break;

    }
    setReloadFlag(true);
    setIsChange(true);
  }, []);
  // -- テキストエリア --
  const handlerChangeTextareaContents = useCallback((v) => {
    setTextAreaContents(v);
    setReloadFlag(true);
  }, []);
  // -- 決定ボタン --
  const handlerClickDecision = useCallback(() => {
    TrimUiManager.ins.emit(
      'r->t:reload-text',
      {
        textParam: {
          id,
          text: textAreaContents,
          color: textColor,
          alpha: textTransparency / 100,
          rotation: Number(textRotation),
          textAlign: textAlignList[Number(textAlign)].value as TrimTextAlignStrType,
          lineHeight: lineSpacingNum,
          font: {
            isBold: isBold ? 'bold' : '',
            isItalic: isItalic ? 'italic' : '',
            // size: fontSize,
            size: fontSize,
            family: fontList.find((v) => v.value === font)?.label || fontList[0]?.label || '',
          },
          options: {
            fontType: 'server',
            isShadow: dropShadow,
            shadowTransparency: `rgba(0, 0, 0, ${transparency / 100})`,
            offset: distance,
            outlineColor: outlineColor,
            outline: 0,
          }
        },
        callback: () => {
          setPrevText(textAreaContents);
          setIsInit(false);
        },
      },
    );
  }, [
    id, textAreaContents, font, fontList, fontSize, textColor, isBold, isItalic, transparency, textTransparency,
    outlineWidth, outlineColor, distance, textAlign, textRotation, lineSpacingNum, dropShadow, isInit,
  ]);

  // -- Life Cycle --
  // --- did mount --
  useEffect(() => {
      // ---- フォントアクセス ----
      TrimUiManager.ins.emit(
        't->r:web-font:get-list',
        {
          callback: (e: { fonts: string[] }) => {
            const _fontList = e.fonts.map((v, i) => ({ value: v, label: v }));
            setFontList(_fontList);
            const _font = fontList.find((v) => v.label === font) ?? (fontList[0] ?? { value: '', label: '' });
            if (fontList.length) setFont(_font.label);
          },
        },
      );
  }, []);

  // ---- edit用のデータセット ----
  useEffect(() => {
    setId(info.id);
    setIsBold(info.isBold);
    setIsItalic(info.isItalic);
    setTextColor(info.textColor);
    setIsOnDropper(info.isOnDropper);
    setFont(info.font);
    setFontSize(info.fontSize + 'pt');
    setFontSizeView(info.fontSize + 'pt');
    setTextTransparency(info.textTransparency);
    setDropShadow(info.dropShadow);
    setTransparency(info.transparency);
    setDistance(info.distance);
    setOutlineWidth(info.outlineWidth);
    setOutlineColor(info.outlineColor);
    setTextAlign(info.textAlign);
    setTextRotation(String(info.textRotation));
    setTextSelectRotation(String(info.textSelectRotation));
    setLineSpacingNum(info.lineSpacingNum);
    setTextAreaContents(info.textAreaContents);
    setPrevText(info.textAreaContents);
    if (info.textAreaContents.length) {
      setIsInit(false);
    } else {
      setIsInit(true);
    }
  }, [info]);
  useEffect(() => {
    // ---- Chromeブラウザかの判定処理 ----
    setIsChrome(userAgent.indexOf('chrome') != -1);
    // ---- 初期位置見切れ対応 ----
    const bodyRect = body.current?.getBoundingClientRect();
    const bodyBottom = (bodyRect?.y || 0) + (bodyRect?.height || 0);
    const bodyRight = (bodyRect?.x || 0) + (bodyRect?.width || 0);
    if (bodyRect) {
      if (bodyBottom > window.innerHeight) {
        setFixedTop((info.pos?.y ?? bodyRect.y) - bodyRect.height - (info.height ? 7 : Number(info.fontSize) * 1.25));  // 7 ... 微調整用
      }
      if (bodyRight > window.innerWidth) {
        setFixedRight(Number(info.fontSize));
      }
    }
    TrimUiManager.ins.emit('r->t:stop-keyboard-event', true);
    return (() => {
      TrimUiManager.ins.emit('r->t:stop-keyboard-event', false);
    });
  }, []);
  // -- テキストエリアボタン以外の更新処理 --
  useEffect(() => {
    if (!isInit && isChange && prevText.length) {
      TrimUiManager.ins.emit(
        'r->t:reload-text',
        {
          textParam: {
            id,
            text: textAreaContents,
            color: textColor,
            alpha: textTransparency / 100,
            rotation: Number(textRotation),
            textAlign: textAlignList[Number(textAlign)].value as TrimTextAlignStrType,
            lineHeight: lineSpacingNum,
            font: {
              isBold: isBold ? 'bold' : '',
              isItalic: isItalic ? 'italic' : '',
              // size: fontSize,
              size: fontSize,
              family: fontList.find((v) => v.value === font)?.label || fontList[0]?.label || '',
            },
            options: {
              fontType: 'server',
              isShadow: dropShadow,
              shadowTransparency: `rgba(0, 0, 0, ${transparency / 100})`,
              offset: distance,
              outlineColor: outlineColor,
              outline: 0,
            },
          }
        },
      );
    }
  }, [
    id, font, fontList, fontSize, fontSizeView, textColor, isBold, isItalic, transparency, textTransparency,
    outlineWidth, outlineColor, distance, textAlign, textRotation, lineSpacingNum, dropShadow,
    isChange, prevText, isInit,
  ]);

  return (
    <div
      ref={container}
      className="trim_free_text_ui__dimmer"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        (!isOnDropper && onBlur) && popCallback();
      }}
    >
      <div
        className="trim_free_text_ui"
        style={{
          ...style,
          top: fixedTop ? fixedTop : style?.top,
          ...fixedRight ? { left: 'auto' } : {},
          right: fixedRight ? fixedRight : style?.right,
        }}
        onClick={(e) => {
          e.stopPropagation();
          // e.preventDefault();
        }}
        ref={body}
      >
        <div className="row1">
          <div className="row1__col1">
            <div className="left_label_form">
              <span>文字色</span>
              <ColorPicker
                selectColor={textColor}
                onGetColor={(color) => {
                  setTextColor(color);
                  setReloadFlag(true);
                  setIsChange(true);
                }}
                tooltip={{ type: 'normal', text: ['文字列の色を設定します'] }}
              />
            </div>
              <Button
                onClick={() => handlerClickDropper(!isOnDropper)}
                color="light"
                icon={<img
                  src={iconDropper}
                  alt=""
                />}
              />
            {/*<Button*/}
            {/*  icon={<i className="fas fa-bold" />}*/}
            {/*  onClick={handlerClickBold}*/}
            {/*  color="light"*/}
            {/*  className="btn_bold"*/}
            {/*  tooltip={{ type: 'normal', text: ['文字列の太さを設定/解除します'] }}*/}
            {/*/>*/}
            {/*<Button*/}
            {/*  icon={<i className="fas fa-italic" />}*/}
            {/*  onClick={handlerClickItalic}*/}
            {/*  color="light"*/}
            {/*  className="btn_italic"*/}
            {/*  tooltip={{ type: 'normal', text: ['文字列の斜体を設定/解除します'] }}*/}
            {/*/>*/}
            {/*<ColorPicker*/}
            {/*  selectColor={textColor}*/}
            {/*  onGetColor={(color) => {*/}
            {/*    setTextColor(color);*/}
            {/*    setReloadFlag(true);*/}
            {/*    setIsChange(true);*/}
            {/*  }}*/}
            {/*  tooltip={{ type: 'normal', text: ['文字列の色を設定します'] }}*/}
            {/*/>*/}
            {/*<Button*/}
            {/*  onClick={() => handlerClickDropper(!isOnDropper)}*/}
            {/*  color="light"*/}
            {/*  icon={<img*/}
            {/*    src={iconDropper}*/}
            {/*    alt=""*/}
            {/*  />}*/}
            {/*/>*/}
          </div>
          <div className="row1__col2">
            <div className="left_label_form">
              <span>フォント選択</span>
              <div>
                <Select
                  value={font}
                  list={fontList}
                  onChange={(e) => handlerChangeFontType(e.target.value)}
                  tooltip={{ type: 'normal', text: ['文字列のフォントを設定します'] }}
                />
                <div className="font_size">
                  <Input
                    value={fontSizeView}
                    onChange={(e) => setFontSizeView(e.target.value)}
                    onBlur={(e) => handlerBlurFontSize(e.target.value)}
                    hoverTooltip={{ messages: ['フォントのサイズを1 ~ 600pointの半角で設定します'], error: false }}
                  />
                  <Select
                    value={selectFontSize ? selectFontSize.value : fontSize}
                    list={selectFontSize ? fontSizeList : [{ value: fontSize, label: fontSize }, ...fontSizeList]}
                    onChange={(e) => handlerChangeTextSize(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row3">
          <div className="row3__col1">
            <div className="left_label_form">
              <span>文字揃え</span>
              <Select
                value={textAlignList[Number(textAlign)].value}
                list={textAlignList}
                onChange={(e) => handlerChangeTextAlign(e.target.value)}
                tooltip={{ type: 'normal', text: ['文字揃えを設定します'] }}
              />
            </div>
          </div>
          {/*<div className="row3__col3">*/}
          {/*  <div className="left_label_form">*/}
          {/*    <span>文字アウトライン幅</span>*/}
          {/*    <NumUpDown*/}
          {/*      value={outlineWidth}*/}
          {/*      min={0}*/}
          {/*      onBlur={(e) => handlerChangeOutlineWidth(e.target.value)}*/}
          {/*      callback={num => handlerChangeOutlineWidth(num)}*/}
          {/*      tooltip={{ type: 'normal', text: ['文字列のアウトライン幅を設定します'] }}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*  <div className="left_label_form">*/}
          {/*    <span>文字アウトライン色</span>*/}
          {/*    <ColorPicker*/}
          {/*      selectColor={outlineColor}*/}
          {/*      onGetColor={(color) => {*/}
          {/*        setReloadFlag(true);*/}
          {/*        setIsChange(true);*/}
          {/*        setOutlineColor(color);*/}
          {/*      }}*/}
          {/*      tooltip={{ type: 'normal', text: ['文字列のアウトライン色を設定します'] }}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*</div>*/}
        </div>

        <div className="row4">
          {/*<TextArea*/}
          {/*  value={textAreaContents}*/}
          {/*  onChange={(e) => handlerChangeTextareaContents(e.target.value)}*/}
          {/*  style={{*/}
          {/*    fontFamily: font,*/}
          {/*  }}*/}
          {/*/>*/}
          <Input
            value={textAreaContents}
            onChange={(e) => handlerChangeTextareaContents(e.target.value)}
            style={{
              fontFamily: font,
            }}
          />
        </div>
        <div className="row5">
          <small>※この入力項目は改行できません。1行でご入力ください。</small>
        </div>

        <div className="trim_free_text_ui__footer">
          <div className="trim_free_text_ui__footer__left">
            <Button
              label="閉じる"
              onClick={() => {
                TrimUiManager.ins.emit('r->t:finish-text-edit', { id });
                popCallback && popCallback();
              }}
            />
          </div>
          <div className="trim_free_text_ui__footer__right">
            <Button
              label="削除"
              onClick={() => {
                handlerClickDelete();
                // popCallback && popCallback();
              }}
              color="danger"
              tooltip={{ type: 'normal', text: ['設定した文字列を削除します'] }}
            />
            <Button
              label="反映"
              disabled={!textAreaContents.length || !reloadFlag}
              onClick={() => {
                handlerClickDecision();
                setReloadFlag(false);
              }}
              tooltip={{ type: 'normal', text: ['入力した文字列をページ内のテキストボックス', 'にコピーします'] }}
            />
          </div>

        </div>
      </div>
    </div>
  );
};
